import React from "react";
import moment, { Moment } from "moment";

import Timeline from "./Timeline";
import KanbanBoard from "./KanbanBoard";
import PlayControls from "./PlayControls";
import {
    itemsToBoardState,
    getEvents,
    updateBoard,
    copyBoard,
    BoardState,
    TimelineEvent,
    initBoardBuckets
} from "../tools/events";
import { Labels, Board } from "../types";
import { DataProvider } from "./DataProvider";
import { getItemsMap } from "../tools";

import "./ReplayView.css";

interface Props {
    board: Board;
    labels: Labels;
    columnId: string;
    from: Moment;
    to: Moment;
    showPrevNextEvents: boolean;
    showProgress: boolean;
}

interface State {
    prevPropsBoard: Board;
    prevPropsColumnId: string;
    startBoardState: BoardState;
    currentBoardState: BoardState;
    events: TimelineEvent[];
    currentEvent: number;
    itemsMovedFrom: { [itemId: string]: string | null };
    addedCount: { [labelId: string]: number };
    removedCount: { [labelId: string]: number };
    time: Moment;
    isPlaying: boolean;
    noEventsWithinFromTo: boolean;
}

export default class ReplayView extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = ReplayView.getStateFromProps(props);
    }

    static getStateFromProps = (props: Props): State => {
        const now = moment();
        const currentBoard = itemsToBoardState(props.board, now);
        const { startBoard, events } = getEvents(currentBoard, props.board.activity_logs, props.columnId, "status");
        initBoardBuckets(props.labels.orderedIds, startBoard, props.columnId);

        const currentBoardState = copyBoard(startBoard);
        const { eventIdx: currentEvent } = updateBoard(currentBoardState, events, 0, props.from, props.columnId);

        // check if there's any event within from - to dates
        let noEventsWithinFromTo;
        if (currentEvent === 0) {
            if (events.length > 0) {
                noEventsWithinFromTo = !events[0].date.isSameOrBefore(props.to);
            } else {
                noEventsWithinFromTo = true;
            }
        } else {
            noEventsWithinFromTo = false;
        }

        return {
            prevPropsBoard: props.board,
            prevPropsColumnId: props.columnId,
            startBoardState: startBoard,
            currentBoardState,
            events,
            currentEvent,
            itemsMovedFrom: {},
            addedCount: {},
            removedCount: {},
            time: props.from,
            noEventsWithinFromTo,
            isPlaying: false
        };
    };

    static getDerivedStateFromProps(props: Props, state: State): State | null {
        if (props.board !== state.prevPropsBoard || props.columnId !== state.prevPropsColumnId) {
            return ReplayView.getStateFromProps(props);
        } else {
            return null;
        }
    }

    componentDidMount(): void {
        this.updateTime(this.props.from);
    }

    setIsPlaying = (isPlaying: boolean): void => {
        this.setState({ isPlaying });
    };

    updateTime = (newTime: Moment, isAnimation?: boolean, animateSingleItemId?: string): boolean => {
        const { startBoardState, currentBoardState, currentEvent, events, time } = this.state;
        const { from, to, columnId } = this.props;

        let useEventIdx = currentEvent;
        let useBoardState = currentBoardState;
        let useNewTime = newTime;

        if (useNewTime.isAfter(to)) {
            useNewTime = to;
        }
        if (useNewTime.isBefore(from)) {
            useNewTime = from;
        }

        if (useNewTime.isSame(time)) {
            return false;
        }

        const newState: Partial<State> = {};

        if (useNewTime.isSameOrAfter(useBoardState.date)) {
            const { eventIdx, itemsMovedFrom, addedCount, removedCount } = updateBoard(
                useBoardState,
                events,
                useEventIdx!,
                useNewTime,
                columnId
            );

            useEventIdx = eventIdx;
            if (isAnimation) {
                if (animateSingleItemId) {
                    Object.assign(newState, {
                        itemsMovedFrom: {
                            [animateSingleItemId]: itemsMovedFrom[animateSingleItemId]
                        },
                        addedCount: {},
                        removedCount: {}
                    });
                } else {
                    Object.assign(newState, {
                        itemsMovedFrom,
                        addedCount,
                        removedCount
                    });
                }
            } else {
                Object.assign(newState, {
                    itemsMovedFrom: {},
                    addedCount: {},
                    removedCount: {}
                });
            }
        } else {
            // todo: optimise. for now we need to go from beginning of time
            useBoardState = copyBoard(startBoardState);
            const { eventIdx } = updateBoard(useBoardState, events, 0, useNewTime, columnId);
            useEventIdx = eventIdx;
            // cannot animate going back, except if this is single item
            if (isAnimation && animateSingleItemId) {
                Object.assign(newState, {
                    itemsMovedFrom: {
                        [animateSingleItemId]: null
                    },
                    addedCount: {},
                    removedCount: {}
                });
            } else {
                Object.assign(newState, {
                    itemsMovedFrom: {},
                    addedCount: {},
                    removedCount: {}
                });
            }
        }

        Object.assign(newState, {
            currentBoardState: useBoardState,
            currentEvent: useEventIdx,
            time: useNewTime
        });

        this.setState(newState as State);

        return true;
    };

    addTime = (deltaS: number): boolean => {
        return this.updateTime(this.state.time.clone().add(deltaS, "seconds"), true);
    };

    nextEvent = (): boolean => {
        const { currentEvent, events } = this.state;
        if (currentEvent < events.length) {
            return this.updateTime(events[currentEvent].date, true);
        } else {
            return false;
        }
    };

    prevEvent = (): boolean => {
        const { currentEvent, events } = this.state;
        if (currentEvent > 1) {
            // currentEvent - 1 is the date we're at now
            // currentEvent - 2 is previous event
            return this.updateTime(events[currentEvent - 2].date, false);
        } else {
            return false;
        }
    };

    render(): React.ReactNode {
        const {
            startBoardState,
            currentBoardState,
            events,
            time,
            itemsMovedFrom,
            addedCount,
            removedCount,
            isPlaying,
            noEventsWithinFromTo
        } = this.state;
        const { board, from, to, labels, columnId } = this.props;
        if (currentBoardState && events && time) {
            return (
                <DataProvider time={time}>
                    <div className="replay_view">
                        <Timeline
                            startBoard={startBoardState}
                            events={events}
                            labels={labels}
                            columnId={columnId}
                            from={from}
                            to={to}
                            currentTime={time}
                            isPlaying={isPlaying}
                            timeUpdated={this.updateTime}
                        />
                        <PlayControls
                            addTime={this.addTime}
                            nextEvent={this.nextEvent}
                            prevEvent={this.prevEvent}
                            timeUpdated={this.updateTime}
                            setIsPlaying={this.setIsPlaying}
                            from={from}
                            to={to}
                            currentTime={time}
                        />
                        <KanbanBoard
                            boardState={currentBoardState}
                            itemsMovedFrom={itemsMovedFrom}
                            addedCount={addedCount}
                            removedCount={removedCount}
                            items={getItemsMap(board)}
                            labels={labels}
                            timeUpdated={this.updateTime}
                            nextEvent={this.nextEvent}
                            showPrevNextEvents={this.props.showPrevNextEvents}
                            showProgress={this.props.showProgress}
                            columnId={columnId}
                            isPlaying={isPlaying}
                            noEventsWithinFromTo={noEventsWithinFromTo}
                        />
                    </div>
                </DataProvider>
            );
        } else {
            return <div>...</div>;
        }
    }
}
